import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { IconDoc } from './icons'

export function Section({
  title,
  right,
  children,
}: {
  title: string
  right?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="section">
      <h3>
        <span>{title}</span>
        {right}
      </h3>
      {children}
    </div>
  )
}

export function Slider({
  label,
  value,
  min,
  max,
  step = 0.01,
  format,
  ends,
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  step?: number
  format?: (v: number) => string
  /**
   * WHAT THE TWO DIRECTIONS MEAN, instead of what the number is.
   *
   * A figure like "4 of 10" is a measurement of nothing: there is no unit
   * behind it and nobody has an opinion about whether four is right. What a
   * person actually wants to know is which way is which. Reported as: we do
   * not need to write four of ten, put smaller and larger on each side.
   *
   * When this is set the value readout is dropped entirely and the two words
   * sit under the track, one at each end.
   */
  ends?: [string, string]
  onChange: (v: number) => void
}) {
  return (
    <div className="field">
      {/*
        An empty label is allowed, and means the heading above already named
        this control.
      */}
      {(label || !ends) && (
        <div className="field-head" style={label ? undefined : { justifyContent: 'flex-end' }}>
          {label && <label>{label}</label>}
          {!ends && <span className="val">{format ? format(value) : value.toFixed(2)}</span>}
        </div>
      )}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
      />
      {ends && (
        <div className="field-ends">
          <span>{ends[0]}</span>
          <span>{ends[1]}</span>
        </div>
      )}
    </div>
  )
}

export function Seg<T extends string | number>({
  options,
  value,
  onChange,
  wrap,
  accent,
}: {
  options: { value: T; label: string }[]
  value: T
  onChange: (v: T) => void
  wrap?: boolean
  accent?: boolean
}) {
  return (
    <div className={wrap ? 'seg wrap' : 'seg'}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          aria-pressed={o.value === value}
          className={accent ? 'accent' : undefined}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function Check({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="check">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  )
}

/**
 * A ROW OF FIGURES, THE SAME ONE EVERYWHERE.
 *
 * Built to the card the owner showed me from his other product: a very soft
 * gradient from a tinted top corner to white, an icon in a white tile on the
 * right, the number large and black, a caption under it, and a way in along
 * the bottom.
 *
 * WHY THE GRADIENT WORKS THERE AND DID NOT WORK HERE. His reference is barely
 * saturated - the tinted corner is about eight percent of the way to the hue,
 * and it is white before the far edge. Mine started at full mint. The travel
 * was never the problem; the chroma was. Same shape, a tenth of the color.
 *
 * A FIGURE WITH NOWHERE TO GO HAS NO LINK. The card in the reference always
 * carries one because every number there has a screen behind it. Half of ours
 * do not yet, and a View that goes nowhere is worse than no View.
 */
export interface Figure {
  label: string
  value: string
  /** Rendered smaller and grayed, because ft is a word and 48 is the number. */
  unit?: string
  note?: string
  /** The tint on the corner. Four strengths of one hue, never four hues. */
  tone?: 1 | 2 | 3 | 4
  icon?: ReactNode
  /** Only when there is somewhere real to go. */
  action?: { label: string; onClick: () => void }
}

export function Figures({ items }: { items: Figure[] }) {
  if (items.length === 0) return null
  return (
    <div className="figs" style={{ ['--figs' as string]: items.length }}>
      {items.map((f, i) => (
        /*
          THE ICON SITS WITH THE LABEL, IN INK.

          It used to be a colored tile pushed to the far right of the card, so
          four cards in a row carried four different tints and the row read as
          a color key rather than as four numbers. Semantic color belongs on a
          tag or a delta, where it means a state; on a figure card it means
          nothing at all, which is exactly why it competed with the numbers.
          The tone class is kept so nothing that still asks for one breaks.
        */
        <div key={f.label} className={`card pad fig tone-${f.tone ?? Math.min(i + 1, 4)}`}>
          <div className="fig-top">
            {f.icon && <span className="fig-icon">{f.icon}</span>}
            <p className="fig-label">{f.label}</p>
          </div>
          <b className="metric">
            {f.value}
            {f.unit && <small>{f.unit}</small>}
          </b>
          {f.note && <span className="fig-note">{f.note}</span>}
          {f.action && (
            <button type="button" className="fig-go" onClick={f.action.onClick}>
              {f.action.label}
              <i aria-hidden="true">&rsaquo;</i>
            </button>
          )}
        </div>
      ))}
    </div>
  )
}

/**
 * IS THIS A FINGER OR A MOUSE.
 *
 * Asked because the upload screen was telling a man standing in a driveway to
 * drop a photo here. There is no drag and drop on a phone: the first sentence
 * on the first screen of the product was an instruction he could not follow.
 *
 * A media query, not the user agent string, and it re-reads itself: a Surface
 * folded into a tablet, or a phone with a keyboard on it, changes the answer
 * without reloading the page.
 */
export function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(
    () => typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches,
  )
  useEffect(() => {
    if (typeof matchMedia !== 'function') return
    const q = matchMedia('(pointer: coarse)')
    const on = () => setCoarse(q.matches)
    q.addEventListener('change', on)
    return () => q.removeEventListener('change', on)
  }, [])
  return coarse
}

/**
 * ESCAPE CLOSES THE SHEET.

 *
 * Every dialog in this product closed on a backdrop click and on its own X and
 * on nothing else, which is the one keyboard convention people have without
 * being taught it, and the only way out for somebody who opened a full height
 * sheet on a laptop and cannot see the backdrop.
 *
 * Found by a screenshot script that could not get past a packet it had opened,
 * which is exactly the position a person is in.
 */
export function useEscape(onClose: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
}

/* ======================================================================== *
 *  ONE DIALOG, EVERYWHERE.
 *
 *  Ten sheets in this product each wrote their own backdrop, their own panel,
 *  their own close button and their own way out. They looked the same because
 *  they were copied, which is not the same thing as being the same, and the
 *  differences were all in the parts nobody looks at: four of them had no
 *  Escape, none of them told a screen reader they were a dialog, none of them
 *  moved focus, and every one of them closed if you dragged a text selection
 *  out of a field and let go over the backdrop. That last one deletes typed
 *  work, silently, and it was in every sheet in the product.
 *
 *  So this is the dialog now, and the sheets are its contents.
 * ======================================================================== */

/*
 * The lock is counted, not a boolean. A sheet opened from inside another sheet
 * unlocking the page behind BOTH of them on close is how a modal ends up
 * scrolling underneath itself.
 */
let locks = 0
function lockPage() {
  locks += 1
  if (locks === 1) {
    // The scrollbar's width, given back as padding, so the page does not jump
    // sideways the instant a dialog opens.
    const gap = window.innerWidth - document.documentElement.clientWidth
    document.body.style.overflow = 'hidden'
    if (gap > 0) document.body.style.paddingRight = `${gap}px`
  }
}
function unlockPage() {
  locks = Math.max(0, locks - 1)
  if (locks === 0) {
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
  }
}

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'

export function Sheet({
  title,
  /**
   * Replaces the heading, for the one sheet whose top row is a pair of tabs.
   * The title is still required and still what a screen reader announces.
   */
  head,
  /** An extra class on the panel, for the two sheets that hold a table. */
  variant,
  /** Rendered in the footer, right aligned, with the primary action LAST. */
  foot,
  onClose,
  children,
}: {
  title: string
  head?: ReactNode
  variant?: string
  foot?: ReactNode
  onClose: () => void
  children: ReactNode
}) {
  const panel = useRef<HTMLDivElement | null>(null)
  /*
   * WHERE THE PRESS STARTED, not where it ended. A person selecting the text
   * in a field and releasing past the edge of the panel was closing the sheet
   * and losing everything typed into it, because a click is reported on the
   * common ancestor of the press and the release, and that ancestor is the
   * backdrop. Only a press that BEGAN on the backdrop is somebody reaching
   * past the dialog to dismiss it.
   */
  const from = useRef<EventTarget | null>(null)

  useEffect(() => {
    const had = document.activeElement as HTMLElement | null
    lockPage()
    // Focus goes into the sheet, so the first Tab is inside it and a screen
    // reader starts reading the dialog rather than the page behind it.
    panel.current?.focus()
    return () => {
      unlockPage()
      // And comes back to the control that opened it, rather than to the top
      // of a page the person has to find their place in again.
      if (had && document.contains(had)) had.focus()
    }
  }, [])

  /*
   * Escape is on the window rather than on the panel, because a person who
   * clicked the backdrop has moved focus to the body and a handler bound to
   * the panel would have stopped listening to the one key they are about to
   * press.
   */
  useEscape(onClose)

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !panel.current) return

    /*
     * TAB STAYS IN THE DIALOG. Without this it walks straight out into the
     * page underneath, where a keyboard user is now operating controls they
     * cannot see behind a backdrop.
     */
    const stops = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    )
    if (stops.length === 0) return
    const first = stops[0]
    const last = stops[stops.length - 1]
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  return (
    <div
      className="sheet-backdrop"
      onMouseDown={(e) => {
        from.current = e.target
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && from.current === e.currentTarget) onClose()
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={variant ? `sheet-panel ws ${variant}` : 'sheet-panel ws'}
        onKeyDown={onKey}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-top">
          {head ?? <h2>{title}</h2>}

          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
        {children}
        {foot && <div className="ws-actions">{foot}</div>}
      </div>
    </div>
  )
}

/* ======================================================================== *
 *  NOTHING HERE IS THREE DIFFERENT SENTENCES.
 *
 *  One class, ws-empty, was carrying three unrelated meanings: still reading,
 *  nothing yet, and something went wrong. They printed identically, which
 *  means a contractor on a bad connection in a driveway read "Reading your
 *  jobs" in exactly the shape and color that says "you have no jobs" - and
 *  the difference between those two is his whole season.
 *
 *  So they are three things now, and the difference is legible at a glance.
 * ======================================================================== */

/** Still reading. Never a heading, and never in the empty state's shape. */
export function Loading({ what }: { what: string }) {
  return (
    <p className="ws-loading" role="status">
      <span className="ws-spin" aria-hidden="true" />
      {what}
    </p>
  )
}

export function Empty({
  /** The short version, set as a heading. Omitted for filtered and wrong. */
  title,
  children,
  /**
   * NOTHING YET is a teaching state and earns a way to start.
   * NOTHING MATCHES is the filter's fault and earns a way to clear it.
   * SOMETHING WENT WRONG earns neither, because a button that cannot fix it
   * is worse than no button.
   */
  kind = 'none',
  action,
  /**
   * THE GLYPH TILE, and it is optional on purpose.
   *
   * RULES.md asks for a 48px tile with a 22px icon at the left of the row.
   * A screen that has a mark for the thing it is missing should hand it over;
   * a screen that does not gets the tile with a neutral mark rather than a
   * borrowed one, because an icon that means something else is worse than an
   * icon that means nothing.
   */
  icon,
}: {
  title?: string
  children: ReactNode
  kind?: 'none' | 'filtered' | 'wrong'
  action?: { label: string; onClick: () => void }
  icon?: ReactNode
}) {
  /*
   * HORIZONTAL, AND NEVER VERTICALLY CENTERED. RULES.md says so in those
   * words. It had been a centered column with 44px above and below, which on
   * a stretched panel is three hundred pixels of nothing with a sentence
   * floating in the middle of it - which is exactly what an empty screen must
   * not look like, because the state it describes is normal and temporary.
   */
  return (
    <div className={`ws-empty is-${kind}`}>
      <span className="ws-empty-glyph" aria-hidden="true">
        {icon ?? <IconDoc size={22} />}
      </span>
      <span className="ws-empty-say">
        {title && kind === 'none' && <b className="ws-empty-title">{title}</b>}
        <span className="ws-empty-line">{children}</span>
      </span>
      {action && kind !== 'wrong' && (
        <button type="button" className="btn" onClick={action.onClick}>
          {action.label}
        </button>
      )}
    </div>
  )
}

/* ======================================================================== *
 *  SAVED, AND WHEN IT STOPS SAYING SO.
 *
 *  Every screen kept its own copy of this: a string of state, a setTimeout,
 *  and a number that was 3000 in one file and 4000 in the next and absent in
 *  a third, so the same word hung on screen for a different length of time
 *  depending on which screen you were on. None of them cleared the timer on
 *  unmount, so closing a sheet in the second after a save set state on a
 *  component that no longer existed. And none of them were announced, so the
 *  one confirmation in the product that a save happened was invisible to
 *  anybody not looking at that corner of the screen.
 * ======================================================================== */

export interface Said {
  text: string
  tone: 'good' | 'bad' | 'plain'
}

export function useSaid() {
  const [said, set] = useState<Said | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // The timer dies with the component. This is the whole reason the hook
  // exists rather than four lines copied into each screen.
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  const say = useCallback((text: string, tone: Said['tone'] = 'good') => {
    if (timer.current) clearTimeout(timer.current)
    set({ text, tone })
    /*
     * ONE DURATION, AND IT IS LONG ENOUGH TO READ. Four seconds, because the
     * shortest of these is two words and the longest is a full sentence about
     * a file being too big, and a message that leaves before it is read is
     * the same as no message.
     *
     * A failure does not leave on its own. Something did not happen, and the
     * person has to be able to look back at why after they have finished
     * looking at whatever they were doing when it did not happen.
     */
    if (tone === 'bad') return
    timer.current = setTimeout(() => set(null), 4000)
  }, [])

  const clear = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    set(null)
  }, [])

  return { said, say, clear }
}

/** ROLE STATUS, so it is spoken as well as printed. */
export function SaidLine({ said, dirty }: { said: Said | null; dirty?: boolean }) {
  if (!said && !dirty) return null
  return (
    <span className={said ? `ws-said ${said.tone}` : 'ws-said'} role="status">
      {said ? said.text : 'Not saved yet.'}
    </span>
  )
}

/**
 * WHAT THIS SCREEN CANNOT SEE, AS A LIST RATHER THAN AS A PARAGRAPH.
 *
 * Six screens carried the same thing: a bold heading and then every item
 * joined with a space into one block of prose. On Invoices that was six lines
 * of continuous text under the list, which is a wall nobody reads, and a thing
 * a screen cannot see is exactly as much a fact as a thing it can. Today had
 * already solved it - a panel, and a two column list of a bold reason and one
 * sentence - and this is that, made shared so the six agree.
 *
 * Each string is written as "Label. The rest." so the split is the first
 * sentence, and no item had to be rewritten to get a label.
 */
export function NotKnown({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null
  return (
    <div className="card ws-p ws-unknown">
      <div className="ws-ph">
        <h3>{title}</h3>
      </div>
      <ul className="ws-dk">
        {items.map((line) => {
          const cut = line.indexOf('. ')
          const label = cut === -1 ? line : line.slice(0, cut + 1)
          const rest = cut === -1 ? '' : line.slice(cut + 2)
          return (
            <li key={line}>
              <b>{label}</b>
              <span>{rest}</span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
